import { PERMISSIONS } from '../../../constants/permissions';
import { ModuleKey } from './types';

export type PresetKey = 'owner' | 'manager' | 'accountant' | 'apClerk' | 'arClerk' | 'paymentApprover' | 'auditor' | 'basic';

// Ordered widget ids per preset. The Action Center is pinned separately and is not listed here.
//
// `owner` is the premium business-owner board (unlocked per-institution by a SaaS Operator and then
// granted per-user). `basic` is the free minimal board an InstitutionAdmin sees until the premium
// board is unlocked — a single core widget plus the pinned Action Center.
export const ROLE_PRESETS: Record<PresetKey, string[]> = {
    // Whole-business board for the institution admin / owner: today's workforce pulse, then money
    // (books, sales, payroll, spend), stock and people. The headline numbers live in the KPI strip
    // (BoardKpiStrip), so numbers-only *.summary widgets are not on the default board.
    // Widgets for disabled modules or unheld permissions are filtered out at render time.
    owner: [
        'attendance.dayPulse',
        'accounting.trend',
        'accounting.arAging', 'sales.pipeline',
        'payroll.byDepartment', 'expense.byCategory',
        'expense.queue',
        'inventory.valueByStore', 'people.summary',
        'leave.summary', 'attendance.trend',
        // Previous-month money (the "this month" tiles read zero on the 1st). Appended; never reorder above.
        'accounting.lastMonth', 'sales.lastMonth', 'expense.lastMonth',
        // Appended: the user's own machine-breakdown reports (hidden when maintenance is off).
        'my-breakdowns',
        // S4-04: unpaid wages with the legal deadline (hidden without PAYROLL_VIEW or with payroll off).
        'payroll.unpaidWages',
    ],
    // Operations board for an institution manager: workforce first, then the approvals queue and the
    // other departments. Receivables moved to the accountant board (the manager acts on people, not ageing).
    manager: [
        'attendance.dayPulse',
        'attendance.trend', 'leave.summary',
        'expense.queue',
        // No inventory widget: the Institution Manager template has no STOCK_VIEW, so it would be
        // filtered out and leave a blank half row.
        'people.summary', 'sales.pipeline',
        'expense.byCategory',
        'my-breakdowns',
        'payroll.unpaidWages',
    ],
    // Finance home: the books, receivables, spend and last month's closing numbers. The sales pipeline
    // moved to the manager board; the accountant works the invoices, not the funnel.
    accountant: [
        'accounting.trend',
        'accounting.arAging', 'accounting.topReceivables',
        'accounting.expenseByAccount', 'expense.byCategory',
        'expense.queue',
        'accounting.lastMonth', 'sales.lastMonth', 'expense.lastMonth',
        'my-breakdowns',
        'accounting.withheldNotDeposited',
        'payroll.unpaidWages',
    ],
    // AP clerk: where the money went and the trend. expense.queue is listed in the note but needs
    // EXPENSE_CLAIM_VIEW_ALL, which the AP Clerk template lacks, so it is hidden for that role.
    apClerk: ['expense.queue', 'accounting.expenseByAccount', 'accounting.trend', 'accounting.withheldNotDeposited'],
    // AR clerk: who owes what and for how long, then sales revenue and the pipeline.
    arClerk: ['accounting.arAging', 'accounting.topReceivables', 'sales.revenue', 'sales.pipeline'],
    // Payment approver: money in and out, receivables ageing, claims awaiting a decision.
    paymentApprover: ['accounting.trend', 'accounting.arAging', 'expense.queue', 'accounting.withheldNotDeposited', 'payroll.unpaidWages'],
    // Auditor: read-only picture of the books.
    auditor: ['accounting.trend', 'accounting.lastMonth', 'accounting.expenseByAccount', 'accounting.arAging'],
    basic: ['attendance.dayPulse'],
};

// Role → default board when the user has no explicit DASHBOARD_VIEW_* grants.
// The owner board is NEVER a role default: it is premium-gated and granted per-user, so an
// InstitutionAdmin falls back to the free `basic` board until the premium board is unlocked and granted.
export function defaultPreset(has: (key: string) => boolean): PresetKey {
    // BASE keyed this off mutually-exclusive role membership (an InstitutionAdmin held neither
    // the Accountant nor the InstitutionManager role token, so both checks fell through to
    // 'basic'). Under the permission-key system InstitutionAdmin's admin wildcard holds EVERY
    // catalog key, including both DASHBOARD_VIEW_MANAGER and DASHBOARD_VIEW_ACCOUNTANT — a
    // combination no other legacy role can hold (InstitutionManager holds only the manager key,
    // Accountant only the accountant key) — so that combination is the admin case and must fall
    // through to 'basic' exactly like BASE, checked before either single-key branch.
    if (isAdminKeyCombination(has)) return 'basic';
    if (has(PERMISSIONS.DASHBOARD_VIEW_MANAGER)) return 'manager';
    if (has(PERMISSIONS.DASHBOARD_VIEW_ACCOUNTANT)) return 'accountant';
    // Finance role boards, after manager and accountant: a user holding one of those keeps that board.
    if (has(PERMISSIONS.DASHBOARD_VIEW_PAYMENT_APPROVAL)) return 'paymentApprover';
    if (has(PERMISSIONS.DASHBOARD_VIEW_PAYABLES)) return 'apClerk';
    if (has(PERMISSIONS.DASHBOARD_VIEW_RECEIVABLES)) return 'arClerk';
    if (has(PERMISSIONS.DASHBOARD_VIEW_AUDIT)) return 'auditor';
    return 'basic';
}

/**
 * True for the institution admin's wildcard: it holds every catalog key, so it holds BOTH the manager
 * and the accountant board key, a combination no suggested role has. Such a user keeps the whole-
 * business board and tiles (never one role's slice).
 */
export function isAdminKeyCombination(has: (key: string) => boolean): boolean {
    return has(PERMISSIONS.DASHBOARD_VIEW_MANAGER) && has(PERMISSIONS.DASHBOARD_VIEW_ACCOUNTANT);
}

/**
 * Preset that drives the KPI strip and the Action Center. The admin wildcard sees today's whole-
 * business tiles ('owner'); everyone else sees the tiles of their own board.
 */
export function stripPresetFor(primary: PresetKey, has: (key: string) => boolean): PresetKey {
    return isAdminKeyCombination(has) ? 'owner' : primary;
}

/**
 * The admin wildcard's home widgets when it has no saved layout (and after "Reset to default").
 * The admin holds every board key, so `primary` resolves to 'manager'; before the role-dashboard
 * change that meant the manager list below. The re-chosen manager list is for the real Institution
 * Manager only, so the admin keeps this list exactly as it was on develop 6409b8165. Frozen: do not
 * edit it when the manager preset changes.
 */
export const ADMIN_HOME_FALLBACK: readonly string[] = [
    'attendance.dayPulse',
    'attendance.trend', 'leave.summary',
    'people.summary', 'sales.pipeline',
    'expense.byCategory', 'accounting.arAging',
    'expense.queue',
    // Appended (the list above is otherwise frozen): the user's own machine-breakdown reports.
    'my-breakdowns',
    // S4-04: appended; the list above stays frozen.
    'payroll.unpaidWages',
];

/**
 * Fallback widget list of the home board. The owner board and every single-role board use their own
 * preset; the admin wildcard (on any board other than the premium owner board) keeps its old list.
 */
export function homeFallbackFor(primary: PresetKey, has: (key: string) => boolean): string[] {
    if (primary !== 'owner' && isAdminKeyCombination(has)) return [...ADMIN_HOME_FALLBACK];
    return ROLE_PRESETS[primary];
}

// Default widget order for a module's deep board. `ModuleBoard` uses the matching array as its
// fallback layout when the user has no saved layout for that board_key. Every gated module is
// populated with chart/list widgets (the module's headline numbers are the BoardKpiStrip above the
// grid, so numbers-only *.summary widgets stay in the registry/picker but not in the default); md
// widgets are paired so a row never leaves a blank half. `holidays` has no deep board.
export const MODULE_DEFAULT_PRESETS: Record<ModuleKey, string[]> = {
    attendance: [
        'attendance.kpis', 'attendance.dayPulse', 'attendance.composition', 'attendance.arrivals',
        'attendance.weekday', 'attendance.topLate', 'attendance.topOt', 'attendance.department',
        'attendance.todayLog',
    ],
    leave: ['leave.summary', 'leave.byType', 'leave.trend'],
    // Workforce overview (the HR manager's landing board): today's pulse, headcount, leave
    // approvals, then trends. Attendance/leave widgets hide themselves for roles that can't see them.
    people: [
        'attendance.dayPulse',
        'people.summary', 'leave.summary',
        'attendance.trend', 'leave.byType',
        'people.growth',
        'attendance.department',
        // S4-04: appended; shown only to people who hold PAYROLL_VIEW.
        'payroll.unpaidWages',
    ],
    holidays: [],
    accounting: ['accounting.trend', 'accounting.arAging', 'accounting.topReceivables', 'accounting.expenseByAccount'],
    inventory: ['inventory.valueByStore', 'inventory.topItems', 'inventory.movement', 'inventory.reorder'],
    sales: ['sales.pipeline', 'sales.outcomes', 'sales.revenue', 'sales.topDeals'],
    // S4-13: the 48/60-hour week warning appended; says how to turn it on while it is off.
    payroll: ['payroll.trend', 'payroll.byDepartment', 'payroll.overtime', 'payroll.unpaidWages', 'payroll.otHoursWarning'],
    expense: ['expense.trend', 'expense.byCategory', 'expense.topClaimants', 'expense.queue'],
    // No deep board and no widgets exist for merchandising — following the `holidays` precedent.
    merchandising: [],
    // T9: commercial joined the gated ModuleKey set (see ../types.ts) but has no dashboard
    // widgets of its own yet — no widget registry entries exist for it, so it has no deep board,
    // exactly like holidays above. Not a widget-building task.
    commercial: [],
    // Maintenance joined the gated ModuleKey set in L0; L4 appends the self-service my-breakdowns widget.
    maintenance: ['my-breakdowns'],
    // Fixed assets has no dashboard widgets (dashboards frozen); no deep board, like holidays.
    fixed_assets: [],
    // 2026-10-23 menu switches: no deep boards.
    shifts: [], procurement: [], tax: [], insurance: [], tasks: [], notices: [], reports: [],
    // Add-ons (help, feedback, xero), not modules with boards.
    help: [], feedback: [], xero: [],
};
